import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { formatNumber } from "utils/utils";
import type {
  TipsSummaryFilter,
  TipsSummaryModel,
} from "../logic/tips.v2.logic";
import {
  SummaryCard,
  SummaryCards,
  SummaryIcon,
  SummaryLabel,
  SummaryValue,
} from "../tips.styled";

interface TipsSummaryProps {
  readonly activeFilter: TipsSummaryFilter;
  readonly onFilterChange: (filter: TipsSummaryFilter) => void;
  readonly summary: TipsSummaryModel;
}

interface SummaryCardConfig {
  readonly amount: (summary: TipsSummaryModel) => number;
  readonly filter: TipsSummaryFilter;
  readonly iconName: string;
  readonly labelKey: string;
}

const summaryCards: readonly SummaryCardConfig[] = [
  {
    amount: (summary) => summary.allTipsAmount,
    filter: "all",
    iconName: "v2/dollar_circle",
    labelKey: "Tips.AllTips",
  },
  {
    amount: (summary) => summary.readyToPayoutAmount,
    filter: "readyToPayout",
    iconName: "v2/chart",
    labelKey: "Tips.ReadyToBePaidOut",
  },
  {
    amount: (summary) => summary.paidOutAmount,
    filter: "paidOut",
    iconName: "v2/checkmark",
    labelKey: "Tips.PaidOut",
  },
] as const;

export const TipsSummary = (props: TipsSummaryProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <SummaryCards>
      {summaryCards.map((card) => {
        const isActive = props.activeFilter === card.filter;

        return (
          <SummaryCard
            key={card.filter}
            $isPrimary={isActive}
            aria-pressed={isActive}
            type="button"
            onClick={() => props.onFilterChange(card.filter)}
          >
            <SummaryIcon $isPrimary={isActive}>
              <CustomIcon
                name={card.iconName}
                size="32px"
                color={isActive ? ColorV2.White : ColorV2.Blue1}
              />
            </SummaryIcon>
            <div>
              <SummaryValue $isPrimary={isActive}>
                {formatNumber(card.amount(props.summary))} {t("Tips.Currency")}
              </SummaryValue>
              <SummaryLabel $isPrimary={isActive}>
                {t(card.labelKey)}
              </SummaryLabel>
            </div>
          </SummaryCard>
        );
      })}
    </SummaryCards>
  );
};
